import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Shows a running total (unit price x quantity) below the quantity stepper,
 * with a struck-through compare-at total and a badge when the selected
 * variant has a discount. Updates live as the shopper changes quantity.
 *
 * 3-Pair Pack: on eligible sock products (PACK_HANDLES below) every
 * 3rd pair gets 60% off, which mirrors the automatic "3-Pair Pack" discount
 * in Shopify (buy 2, get 1 at 60% off). So 3 x £25 shows £60, 4 shows £85,
 * 6 shows £130, matching what the cart will charge.
 *
 * @typedef {Object} HbQuantityTotalPriceRefs
 * @property {HTMLElement} [total]
 * @property {HTMLElement} [compare]
 * @property {HTMLElement} [saveBadge]
 *
 * @extends {Component<HbQuantityTotalPriceRefs>}
 */
class HbQuantityTotalPrice extends Component {
  #abortController = new AbortController();

  static PACK_SIZE = 3;
  static PACK_DISCOUNT_ON_ONE = 0.6;
  // Must match the products in the "3-Pair Pack" automatic discount in Shopify.
  static PACK_HANDLES = [
    'peruvian-alpaca-everyday-socks',
    'fine-gauge-merino-wool-sock',
    'peruvian-alpaca-lounge-socks',
  ];

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#abortController;
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleUpdate, { signal });
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @param {Event} event
   */
  #handleUpdate = (event) => {
    const form = this.closest('product-form-component');
    if (!form || !(event.target instanceof Node) || !form.contains(event.target)) return;
    this.#update();
  };

  #getCurrentQuantity() {
    const form = this.closest('product-form-component');
    const quantityInput = /** @type {HTMLInputElement | null} */ (form?.querySelector('input[name="quantity"]'));
    return quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;
  }

  #isPackEligible() {
    const form = this.closest('product-form-component');
    const url = form?.getAttribute('data-product-url') || window.location.pathname;
    const match = url.match(/\/products\/([^/?#]+)/);
    return !!match && HbQuantityTotalPrice.PACK_HANDLES.includes(match[1]);
  }

  #update() {
    const unitPrice = Number(this.dataset.unitPrice || 0);
    const compareAtPrice = Number(this.dataset.compareAtPrice || 0);
    const format = this.dataset.moneyFormat || '${{amount}}';
    const currency = this.dataset.currency || 'USD';
    const packEligible = this.#isPackEligible();
    const quantity = this.#getCurrentQuantity();

    const fullTotal = unitPrice * quantity;
    const packs = packEligible ? Math.floor(quantity / HbQuantityTotalPrice.PACK_SIZE) : 0;
    const packSaving = packs * Math.round(unitPrice * HbQuantityTotalPrice.PACK_DISCOUNT_ON_ONE);
    const total = fullTotal - packSaving;

    const hasCompareAt = compareAtPrice > unitPrice;
    const hasPack = packs > 0;
    const compareTotal = hasCompareAt ? compareAtPrice * quantity : fullTotal;
    const showCompare = hasCompareAt || hasPack;

    if (this.refs.total) {
      this.refs.total.textContent = formatMoney(total, format, currency);
    }

    if (this.refs.compare) {
      this.refs.compare.hidden = !showCompare;
      if (showCompare) {
        this.refs.compare.textContent = formatMoney(compareTotal, format, currency);
      }
    }

    if (this.refs.saveBadge) {
      this.refs.saveBadge.hidden = !showCompare;
      if (hasPack) {
        this.refs.saveBadge.textContent = '3-Pair Pack';
      } else if (hasCompareAt) {
        const discountPct = Math.round(((compareAtPrice - unitPrice) / compareAtPrice) * 100);
        this.refs.saveBadge.textContent = `Save ${discountPct}%`;
      }
    }
  }
}

if (!customElements.get('hb-quantity-total-price')) {
  customElements.define('hb-quantity-total-price', HbQuantityTotalPrice);
}
